(() => {
  'use strict';

  // Mezetto reader v2.2 — mobile audio unlock + focal word position restore.

  const SELECTOR = '[data-mezetto-reader]';
  const THEMES = ['paper', 'sepia', 'night'];
  const FONT_MIN = 0.85;
  const FONT_MAX = 1.35;
  const FONT_STEP = 0.1;
  const MAX_CHAPTERS = 500;
  const MAX_PARAGRAPHS = 30000;
  const FETCH_TIMEOUT_MS = 30000;
  const SUPPORTED_SCHEMA_MAJOR = '3';
  const INLINE_TAGS = new Set(['A', 'B', 'BR', 'CITE', 'EM', 'I', 'MARK', 'Q', 'S', 'SMALL', 'SPAN', 'STRONG', 'SUB', 'SUP', 'U']);

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const debounce = (callback, wait = 180) => {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), wait);
    };
  };

  const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;
  const isInteger = (value) => Number.isInteger(value);
  const isAnchor = (value) => isNonEmptyString(value) && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

  const normalizeFileUrl = (value) => {
    const url = String(value || '').trim();
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    return url;
  };

  const normalizeSha256 = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^sha-?256:/, '')
    .replace(/[^a-f0-9]/g, '');

  const bufferToHex = (buffer) => Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  const cssEscape = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);
  };

  const safeHref = (value) => {
    const href = String(value || '').trim();
    if (!href) return '';
    if (href.startsWith('#') || href.startsWith('/')) return href;

    try {
      const parsed = new URL(href, window.location.origin);
      if (['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) return parsed.href;
    } catch (error) {
      return '';
    }

    return '';
  };

  const sanitizeInlineHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = String(html ?? '');

    const walker = document.createTreeWalker(
      template.content,
      NodeFilter.SHOW_ELEMENT,
    );
    const elements = [];

    while (walker.nextNode()) elements.push(walker.currentNode);

    elements.forEach((element) => {
      if (!INLINE_TAGS.has(element.tagName)) {
        element.replaceWith(...Array.from(element.childNodes));
        return;
      }

      const attributes = Array.from(element.attributes);
      attributes.forEach((attribute) => element.removeAttribute(attribute.name));

      if (element.tagName === 'A') {
        const original = attributes.find((attribute) => attribute.name.toLowerCase() === 'href')?.value || '';
        const href = safeHref(original);
        if (href) {
          element.setAttribute('href', href);
          element.setAttribute('rel', 'noopener noreferrer');
        } else {
          element.replaceWith(...Array.from(element.childNodes));
        }
      }
    });

    return template.innerHTML;
  };

  class MezettoEbookReader {
    constructor(root) {
      if (!root || ['initializing', 'true'].includes(root.dataset.readerReady)) return;

      root.dataset.readerReady = 'initializing';
      root.setAttribute('aria-busy', 'true');
      this.root = root;
      this.storageKey = root.dataset.storageKey || `mezetto-reader:${window.location.pathname}`;
      this.defaultView = root.dataset.defaultView === 'book' ? 'book' : 'flow';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.contentSource = root.dataset.contentSource === 'file' ? 'file' : 'metaobjects';
      this.contentUrl = normalizeFileUrl(root.dataset.contentUrl);
      this.expectedSchemaVersion = String(root.dataset.contentSchemaVersion || '').trim();
      this.expectedEditionVersion = String(root.dataset.contentEditionVersion || '').trim();
      this.expectedSha256 = normalizeSha256(root.dataset.contentSha256);
      this.expectedChapterCount = Number.parseInt(root.dataset.contentChapterCount || '', 10);
      this.expectedBookHandle = String(root.dataset.contentBookHandle || '').trim();

      this.flow = root.querySelector('[data-reader-flow]');
      this.book = root.querySelector('[data-reader-book]');
      this.progressBar = root.querySelector('[data-reader-progress]');
      this.status = root.querySelector('[data-reader-status]');
      this.toolbarWrap = root.querySelector('[data-reader-toolbar-wrap]') || root.querySelector('.mez-reader__toolbar-wrap');
      this.toc = root.querySelector('[data-reader-toc]');
      this.tocList = root.querySelector('[data-reader-toc-list]');
      this.dynamicContent = root.querySelector('[data-reader-dynamic-content]');
      this.contentState = root.querySelector('[data-reader-content-state]');
      this.spread = root.querySelector('[data-reader-spread]');
      this.leftPage = root.querySelector('[data-reader-page-left]');
      this.rightPage = root.querySelector('[data-reader-page-right]');
      this.pageLabel = root.querySelector('[data-reader-page-label]');
      this.pageTotal = root.querySelector('[data-reader-page-total]');
      this.continueButton = root.querySelector('[data-reader-action="continue"]');
      this.prevButton = root.querySelector('[data-reader-action="previous-page"]');
      this.nextButton = root.querySelector('[data-reader-action="next-page"]');
      this.fullscreenExitButton = root.querySelector('[data-reader-action="fullscreen-exit"]');

      if (!this.flow || !this.book) {
        root.dataset.readerReady = 'error';
        return;
      }

      this.state = this.loadState();
      this.pages = [];
      this.pageNodes = [];
      this.anchorElements = [];
      this.anchorOffsets = [];
      this.currentAnchor = this.state.anchor || '';
      this.pageIndex = Number.isFinite(this.state.pageIndex) ? this.state.pageIndex : 0;
      this.isPaginating = false;
      this.lastPageWidth = 0;
      this.scrollFrame = null;
      this.rootScrollFrame = null;
      this.statusTimeout = null;
      this.paginationAttempts = 0;
      this.paginationTimeout = null;
      this.isFullscreen = false;
      this.previousFocus = null;
      this.inertedElements = [];
      this.contentReady = false;
      this.contentDocument = null;
      this.abortController = null;
      this.pendingBookPosition = null;
      this.audioContext = null;

      this.bindEvents();
      this.setContentControlsEnabled(false);
      this.initialize().catch((error) => this.showContentError(error));
    }

    async initialize() {
      if (this.contentSource === 'file') {
        await this.loadExternalContent();
      } else {
        this.assertLegacyContent();
      }

      this.prepareContent();
      if (!this.anchorElements.length) {
        throw new Error('Okunabilir bölüm veya paragraf bulunamadı.');
      }

      this.normalizeSavedPosition();
      this.contentReady = true;
      this.root.dataset.contentStatus = 'ready';
      this.root.dataset.readerReady = 'true';
      this.root.setAttribute('aria-busy', 'false');
      this.setContentControlsEnabled(true);
      this.applyState({ restorePosition: true });
      this.refreshAnchorOffsets();
      this.updateContinueButton();
      this.hideContentState();
      this.openFromQueryParameters();
    }

    loadState() {
      const globalTheme = document.documentElement.getAttribute('data-mezetto-theme');
      const fallback = {
        view: this.defaultView,
        theme: globalTheme === 'dark' ? 'night' : 'paper',
        fontScale: 1,
        width: 'comfortable',
        anchor: '',
        bookmark: '',
        pageIndex: 0,
        bookPosition: null,
        soundEnabled: false,
      };

      try {
        const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        if (!saved || typeof saved !== 'object') return fallback;

        return {
          ...fallback,
          ...saved,
          view: saved.view === 'book' ? 'book' : 'flow',
          theme: THEMES.includes(saved.theme) ? saved.theme : fallback.theme,
          fontScale: clamp(Number(saved.fontScale) || 1, FONT_MIN, FONT_MAX),
          width: saved.width === 'wide' ? 'wide' : 'comfortable',
          bookPosition: isPlainObject(saved.bookPosition)
            && isNonEmptyString(saved.bookPosition.anchor)
            && Number.isFinite(Number(saved.bookPosition.offset))
            ? {
              anchor: saved.bookPosition.anchor,
              offset: Math.max(0, Number(saved.bookPosition.offset) || 0),
            }
            : null,
          soundEnabled: saved.soundEnabled === true,
        };
      } catch (error) {
        return fallback;
      }
    }

    persistState() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      } catch (error) {
        // Depolama kapalı olsa da okuyucu çalışmaya devam eder.
      }
    }

    async loadExternalContent() {
      if (!this.contentUrl) {
        throw new Error('Kitap içerik dosyasının CDN adresi bulunamadı.');
      }
      if (!this.dynamicContent || !this.tocList) {
        throw new Error('Dosya tabanlı okuyucu için gerekli HTML hedefleri bulunamadı.');
      }

      this.root.dataset.contentStatus = 'loading';
      this.setContentState('Kitap hazırlanıyor', 'Metin dosyası yükleniyor ve bütünlük denetiminden geçiriliyor.');

      this.abortController = new AbortController();
      const timeoutId = window.setTimeout(() => this.abortController.abort(), FETCH_TIMEOUT_MS);

      let response;
      try {
        response = await fetch(this.contentUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'default',
          headers: { Accept: 'application/json, text/plain;q=0.9, */*;q=0.1' },
          signal: this.abortController.signal,
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('Kitap içerik dosyası zaman aşımına uğradı.');
        }
        throw new Error('Kitap içerik dosyasına erişilemedi.');
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(`Kitap içerik dosyası yüklenemedi (HTTP ${response.status}).`);
      }

      const bytes = await response.arrayBuffer();
      await this.verifyContentDigest(bytes);

      let parsed;
      try {
        const text = new TextDecoder('utf-8', { fatal: true })
          .decode(bytes)
          .replace(/^\uFEFF/, '');
        parsed = JSON.parse(text);
      } catch (error) {
        throw new Error('Kitap içerik dosyası geçerli UTF-8 JSON biçiminde değil.');
      }

      const documentData = this.validateContentDocument(parsed);
      this.renderContentDocument(documentData);
      this.contentDocument = documentData;
    }

    async verifyContentDigest(bytes) {
      if (!this.expectedSha256) return;
      if (this.expectedSha256.length !== 64) {
        throw new Error('Kitap kaydındaki SHA-256 değeri geçersiz.');
      }
      if (!window.crypto?.subtle) {
        console.warn('Mezetto okuyucu: Web Crypto kullanılamadığı için SHA-256 doğrulaması atlandı.');
        return;
      }

      const digest = await window.crypto.subtle.digest('SHA-256', bytes);
      const actual = bufferToHex(digest);
      if (actual !== this.expectedSha256) {
        throw new Error('Kitap içerik dosyasının SHA-256 değeri kayıtla eşleşmiyor.');
      }
    }

    validateContentDocument(documentData) {
      if (!isPlainObject(documentData)) {
        throw new Error('Kitap içerik dosyasının kök yapısı geçersiz.');
      }

      const schemaVersion = String(documentData.schema_version || '').trim();
      if (!schemaVersion) {
        throw new Error('Kitap içerik dosyasında schema_version bulunamadı.');
      }
      if (this.expectedSchemaVersion && schemaVersion !== this.expectedSchemaVersion) {
        throw new Error(`İçerik şema sürümü eşleşmiyor: ${schemaVersion}.`);
      }
      if (schemaVersion.split('.')[0] !== SUPPORTED_SCHEMA_MAJOR) {
        throw new Error(`Desteklenmeyen içerik şeması: ${schemaVersion}.`);
      }

      const bookHandle = String(documentData.book_handle || '').trim();
      if (!isAnchor(bookHandle)) {
        throw new Error('Kitap içerik dosyasında geçerli book_handle bulunamadı.');
      }
      if (this.expectedBookHandle && bookHandle !== this.expectedBookHandle) {
        throw new Error('İçerik dosyasındaki kitap handle değeri bağlı kitap kaydıyla eşleşmiyor.');
      }

      const editionVersion = String(documentData.edition_version || '').trim();
      if (this.expectedEditionVersion && editionVersion !== this.expectedEditionVersion) {
        throw new Error(`İçerik sürümü eşleşmiyor: ${editionVersion || 'boş'}.`);
      }

      const chapters = documentData.chapters;
      if (!Array.isArray(chapters) || chapters.length === 0) {
        throw new Error('Kitap içerik dosyasında bölüm bulunamadı.');
      }
      if (chapters.length > MAX_CHAPTERS) {
        throw new Error(`Bölüm sayısı desteklenen sınırı aşıyor (${MAX_CHAPTERS}).`);
      }
      if (Number.isInteger(this.expectedChapterCount) && this.expectedChapterCount > 0 && chapters.length !== this.expectedChapterCount) {
        throw new Error(`Bölüm sayısı eşleşmiyor: dosyada ${chapters.length}, kitap kaydında ${this.expectedChapterCount}.`);
      }

      const chapterAnchors = new Set();
      const paragraphIds = new Set();
      let paragraphCount = 0;
      let previousOrder = 0;

      const normalizedChapters = chapters.map((chapter, index) => {
        const label = `Bölüm ${index + 1}`;
        if (!isPlainObject(chapter)) throw new Error(`${label} yapısı geçersiz.`);
        if (!isInteger(chapter.order) || chapter.order <= 0 || chapter.order <= previousOrder) {
          throw new Error(`${label} order değeri pozitif ve artan bir tam sayı olmalı.`);
        }
        previousOrder = chapter.order;

        const anchor = String(chapter.anchor || '').trim();
        if (!isAnchor(anchor)) throw new Error(`${label} anchor değeri geçersiz.`);
        if (chapterAnchors.has(anchor) || paragraphIds.has(anchor)) {
          throw new Error(`${label} anchor değeri yineleniyor veya bir paragraf kimliğiyle çakışıyor: ${anchor}.`);
        }
        chapterAnchors.add(anchor);

        const title = String(chapter.title || '').trim();
        if (!title) throw new Error(`${label} başlığı boş olamaz.`);

        const titleOrigin = chapter.title_origin === 'editorial' ? 'editorial' : 'original';
        if (!Array.isArray(chapter.paragraphs) || chapter.paragraphs.length === 0) {
          throw new Error(`${label} içinde paragraf bulunamadı.`);
        }

        const paragraphs = chapter.paragraphs.map((paragraph, paragraphIndex) => {
          paragraphCount += 1;
          if (paragraphCount > MAX_PARAGRAPHS) {
            throw new Error(`Toplam paragraf sayısı desteklenen sınırı aşıyor (${MAX_PARAGRAPHS}).`);
          }
          if (!isPlainObject(paragraph)) {
            throw new Error(`${label}, paragraf ${paragraphIndex + 1} yapısı geçersiz.`);
          }

          const id = String(paragraph.id || '').trim();
          if (!isAnchor(id)) {
            throw new Error(`${label}, paragraf ${paragraphIndex + 1} kimliği geçersiz.`);
          }
          if (paragraphIds.has(id) || chapterAnchors.has(id)) {
            throw new Error(`Yinelenen veya çakışan paragraf kimliği: ${id}.`);
          }
          paragraphIds.add(id);

          if (typeof paragraph.html !== 'string') {
            throw new Error(`${label}, paragraf ${paragraphIndex + 1} html değeri metin olmalı.`);
          }

          return { id, html: paragraph.html };
        });

        return {
          order: chapter.order,
          anchor,
          title,
          title_origin: titleOrigin,
          paragraphs,
        };
      });

      return {
        schema_version: schemaVersion,
        book_handle: bookHandle,
        edition_version: editionVersion,
        language: String(documentData.language || 'tr'),
        chapters: normalizedChapters,
      };
    }

    renderContentDocument(documentData) {
      this.dynamicContent.replaceChildren();
      this.tocList.replaceChildren();

      const chapterFragment = document.createDocumentFragment();
      const tocFragment = document.createDocumentFragment();

      documentData.chapters.forEach((chapter, index) => {
        const section = document.createElement('section');
        section.id = chapter.anchor;
        section.className = 'mez-reader__chapter';
        section.dataset.readerChapter = '';
        section.dataset.chapterIndex = String(index);
        section.dataset.titleOrigin = chapter.title_origin;

        const header = document.createElement('header');
        header.className = 'mez-reader__chapter-header';

        const number = document.createElement('span');
        number.className = 'mez-reader__chapter-number';
        number.textContent = String(chapter.order).padStart(2, '0');

        const heading = document.createElement('h2');
        heading.className = 'mez-reader__chapter-title';
        heading.textContent = chapter.title;

        header.append(number, heading);

        const body = document.createElement('div');
        body.className = 'mez-reader__chapter-body rte';

        chapter.paragraphs.forEach((paragraph) => {
          const element = document.createElement('p');
          element.id = paragraph.id;
          element.innerHTML = sanitizeInlineHtml(paragraph.html);
          body.appendChild(element);
        });

        section.append(header, body);
        chapterFragment.appendChild(section);

        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.readerAnchor = chapter.anchor;

        const tocNumber = document.createElement('span');
        tocNumber.textContent = String(chapter.order).padStart(2, '0');
        button.append(tocNumber, document.createTextNode(chapter.title));
        item.appendChild(button);
        tocFragment.appendChild(item);
      });

      this.dynamicContent.appendChild(chapterFragment);
      this.tocList.appendChild(tocFragment);
      this.root.dataset.contentSchema = documentData.schema_version;
      this.root.dataset.contentEdition = documentData.edition_version;
      this.root.dataset.contentBook = documentData.book_handle;
    }

    assertLegacyContent() {
      if (!this.flow.querySelector('[data-reader-chapter]')) {
        throw new Error('Bu kitap kaydında içerik dosyası veya bağlı bölüm kaydı bulunamadı.');
      }
      this.root.dataset.contentStatus = 'ready';
    }

    setContentState(title, message) {
      if (!this.contentState) return;
      this.contentState.hidden = false;
      this.contentState.innerHTML = '';
      const heading = document.createElement('h3');
      const paragraph = document.createElement('p');
      heading.textContent = title;
      paragraph.textContent = message;
      this.contentState.append(heading, paragraph);
    }

    hideContentState() {
      if (this.contentState) this.contentState.hidden = true;
    }

    showContentError(error) {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Kitap içeriği hazırlanamadı.';

      this.contentReady = false;
      this.root.dataset.readerReady = 'error';
      this.root.dataset.contentStatus = 'error';
      this.root.setAttribute('aria-busy', 'false');
      this.flow.hidden = false;
      this.book.hidden = true;
      this.setContentControlsEnabled(false);
      this.setContentState('Kitap metni yüklenemedi', `${message} Lütfen sayfayı yenileyin; sorun sürerse Mezetto’ya bildirin.`);
      this.announce(message);
      console.error('Mezetto e-kitap okuyucu:', error);
    }

    setContentControlsEnabled(enabled) {
      this.root.querySelectorAll('[data-reader-content-control]').forEach((control) => {
        control.disabled = !enabled;
        control.setAttribute('aria-disabled', String(!enabled));
      });

      if (enabled) this.updateContinueButton();
    }

    normalizeSavedPosition() {
      const anchorExists = (anchor) => Boolean(
        anchor && (
          document.getElementById(anchor)
          || this.root.querySelector(`[data-reader-anchor="${cssEscape(anchor)}"]`)
        )
      );

      if (!anchorExists(this.state.anchor)) this.state.anchor = '';
      if (!anchorExists(this.state.bookmark)) this.state.bookmark = '';
      this.currentAnchor = this.state.anchor || '';
      this.persistState();
    }

    prepareContent() {
      this.pages = [];
      this.pageNodes = [];
      this.anchorElements = [];
      this.anchorOffsets = [];
      this.pageIndex = Number.isFinite(this.state.pageIndex) ? this.state.pageIndex : 0;

      const chapters = Array.from(this.flow.querySelectorAll('[data-reader-chapter]'));

      chapters.forEach((chapter, chapterIndex) => {
        const nodes = [];
        const title = chapter.querySelector('.mez-reader__chapter-title');
        const body = chapter.querySelector('.mez-reader__chapter-body');
        const bodyRoot = body?.querySelector(':scope > .metafield-rich_text_field') || body;

        if (title) nodes.push(title);
        if (bodyRoot) {
          const bodyChildren = Array.from(bodyRoot.children);
          if (bodyChildren.length) {
            bodyChildren.forEach((child) => nodes.push(child));
          } else if ((bodyRoot.textContent || '').trim()) {
            nodes.push(bodyRoot);
          }
        }

        nodes.forEach((node, nodeIndex) => {
          const generatedAnchor = `${this.root.id}-c${chapterIndex + 1}-b${nodeIndex + 1}`;
          if (!node.id) node.id = generatedAnchor;
          node.dataset.readerAnchor = nodeIndex === 0 && chapter.id ? chapter.id : node.id;
          node.dataset.readerPageNode = 'true';
          this.pageNodes.push(node);
          this.anchorElements.push(node);
        });

        if (!chapter.dataset.readerAnchor) {
          chapter.dataset.readerAnchor = chapter.id || `${this.root.id}-chapter-${chapterIndex + 1}`;
        }
      });

      this.flow.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => {
            this.refreshAnchorOffsets();
            if (this.state.view === 'book') this.schedulePagination();
          }, { once: true });
        }
      });
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const viewButton = event.target.closest('[data-reader-view-button]');
        if (viewButton) {
          if (!this.contentReady) return;
          this.setView(viewButton.dataset.readerViewButton);
          return;
        }

        const tocButton = event.target.closest('[data-reader-anchor]');
        if (tocButton) {
          if (!this.contentReady) return;
          const anchor = tocButton.dataset.readerAnchor;
          this.setToc(false);
          window.requestAnimationFrame(() => this.goToAnchor(anchor));
          return;
        }

        const actionButton = event.target.closest('[data-reader-action]');
        if (!actionButton) return;
        if (actionButton.tagName === 'A') event.preventDefault();

        const action = actionButton.dataset.readerAction;
        if (!this.contentReady && !['comments', 'fullscreen-exit'].includes(action)) return;
        this.handleAction(action);
      });

      window.addEventListener('scroll', () => {
        if (!this.contentReady || this.isFullscreen || this.state.view !== 'flow' || this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateFlowPosition();
        });
      }, { passive: true });

      this.root.addEventListener('scroll', () => {
        if (!this.contentReady || !this.isFullscreen || this.state.view !== 'flow' || this.rootScrollFrame) return;
        this.rootScrollFrame = window.requestAnimationFrame(() => {
          this.rootScrollFrame = null;
          this.updateFlowPosition();
        });
      }, { passive: true });

      const handleResize = debounce(() => {
        if (!this.contentReady) return;
        if (this.state.view === 'book') {
          this.pendingBookPosition = this.pendingBookPosition
            || this.captureBookPosition()
            || this.state.bookPosition
            || null;
        }
        this.refreshAnchorOffsets();
        if (this.state.view === 'book') this.schedulePagination();
      }, 220);

      window.addEventListener('resize', handleResize, { passive: true });

      document.addEventListener('keydown', (event) => {
        const tag = event.target?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

        if (event.key === 'Escape' && this.toc && !this.toc.hidden) {
          this.setToc(false);
          return;
        }

        if (event.key === 'Escape' && this.isFullscreen) {
          event.preventDefault();
          this.setFullscreen(false);
          return;
        }

        if (!this.contentReady || this.state.view !== 'book') return;
        if (event.key === 'ArrowRight' || event.key === 'PageDown') {
          event.preventDefault();
          this.turnPage(1);
        } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
          event.preventDefault();
          this.turnPage(-1);
        }
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.persistState();
      });
    }

    handleAction(action) {
      switch (action) {
        case 'start':
          this.setView('flow');
          this.goToAnchor(this.getFirstAnchor());
          break;
        case 'continue':
          this.goToAnchor(this.state.bookmark || this.state.anchor);
          break;
        case 'fullscreen-start':
          this.setView('flow', { restorePosition: false });
          this.setFullscreen(true, { anchor: this.getFirstAnchor() });
          break;
        case 'fullscreen':
          this.setFullscreen(!this.isFullscreen);
          break;
        case 'fullscreen-exit':
          this.setFullscreen(false);
          break;
        case 'comments':
          this.goToComments();
          break;
        case 'font-decrease':
          this.setFontScale(this.state.fontScale - FONT_STEP);
          break;
        case 'font-increase':
          this.setFontScale(this.state.fontScale + FONT_STEP);
          break;
        case 'width':
          if (this.state.view === 'book') {
            this.pendingBookPosition = this.pendingBookPosition
            || this.captureBookPosition()
            || this.state.bookPosition
            || null;
          }
          this.state.width = this.state.width === 'wide' ? 'comfortable' : 'wide';
          this.root.dataset.readerWidth = this.state.width;
          this.persistState();
          this.refreshAnchorOffsets();
          if (this.state.view === 'book') this.schedulePagination();
          this.announce(this.state.width === 'wide' ? 'Geniş satır görünümü seçildi.' : 'Rahat satır görünümü seçildi.');
          break;
        case 'theme':
          this.cycleTheme();
          break;
        case 'sound':
          void this.togglePageSound();
          break;
        case 'toc':
          this.setToc(this.toc?.hidden !== false);
          break;
        case 'toc-close':
          this.setToc(false);
          break;
        case 'bookmark':
          this.setBookmark();
          break;
        case 'previous-page':
          this.turnPage(-1);
          break;
        case 'next-page':
          this.turnPage(1);
          break;
        default:
          break;
      }
    }

    openFromQueryParameters() {
      let parameters;
      try {
        parameters = new URLSearchParams(window.location.search);
      } catch (error) {
        return;
      }

      if (parameters.get('reader') !== 'fullscreen') return;

      const requestedChapter = parameters.get('chapter');
      const fallbackAnchor = this.state.anchor || this.getFirstAnchor();
      let targetAnchor = fallbackAnchor;

      if (requestedChapter) {
        const chapterNumber = Number.parseInt(requestedChapter, 10);
        if (String(chapterNumber) === requestedChapter && chapterNumber > 0) {
          targetAnchor = this.flow.querySelectorAll('[data-reader-chapter]')[chapterNumber - 1]?.id || fallbackAnchor;
        } else {
          const requestedTarget = document.getElementById(requestedChapter)
            || this.root.querySelector(`[data-reader-anchor="${cssEscape(requestedChapter)}"]`);
          if (requestedTarget) targetAnchor = requestedChapter;
        }
      }

      window.requestAnimationFrame(() => {
        this.setFullscreen(true, { anchor: targetAnchor, focusExit: false });
      });
    }

    setFullscreen(enabled, { anchor = '', restorePosition = true, focusExit = true, returnFocus = true } = {}) {
      const nextState = Boolean(enabled);
      if (nextState === this.isFullscreen) return;

      const preservedAnchor = anchor || this.currentAnchor || this.state.anchor || this.getFirstAnchor() || '';
      this.isFullscreen = nextState;

      if (nextState) {
        this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.root.dataset.readerFullscreen = 'true';
        this.root.setAttribute('role', 'dialog');
        this.root.setAttribute('aria-modal', 'true');
        document.documentElement.classList.add('mez-reader-fullscreen-active');
        document.body.classList.add('mez-reader-fullscreen-active');
        this.setOutsideContentInert(true);
        this.root.scrollTop = 0;
      } else {
        this.root.dataset.readerFullscreen = 'false';
        this.root.removeAttribute('role');
        this.root.removeAttribute('aria-modal');
        document.documentElement.classList.remove('mez-reader-fullscreen-active');
        document.body.classList.remove('mez-reader-fullscreen-active');
        this.setOutsideContentInert(false);
        this.root.scrollTop = 0;
      }

      this.updateFullscreenControls();

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (this.state.view === 'book') {
            this.prepareBookPagination({ preserveAnchor: true });
          } else {
            this.refreshAnchorOffsets();
            if (restorePosition && preservedAnchor) this.scrollToAnchor(preservedAnchor, false);
            this.updateFlowPosition();
          }

          if (nextState && focusExit) {
            this.fullscreenExitButton?.focus({ preventScroll: true });
          } else if (!nextState && returnFocus && this.previousFocus?.isConnected) {
            this.previousFocus.focus({ preventScroll: true });
          }
        });
      });

      this.announce(nextState ? 'Tam ekran okuma açıldı.' : 'Tam ekran okumadan çıkıldı.');
    }

    updateFullscreenControls() {
      this.root.querySelectorAll('[data-reader-action="fullscreen"]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.isFullscreen));
        button.setAttribute('aria-label', this.isFullscreen ? 'Tam ekran okumadan çık' : 'Tam ekran okumayı aç');
        button.title = this.isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran oku';

        const label = button.querySelector('[data-reader-fullscreen-label]');
        if (label) label.textContent = this.isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran';
      });

      if (this.fullscreenExitButton) {
        this.fullscreenExitButton.hidden = !this.isFullscreen;
      }
    }

    setOutsideContentInert(enabled) {
      if (!('inert' in HTMLElement.prototype)) return;

      if (!enabled) {
        this.inertedElements.forEach(({ element, inert, ariaHidden }) => {
          if (!element?.isConnected) return;
          element.inert = inert;
          if (ariaHidden === null) element.removeAttribute('aria-hidden');
          else element.setAttribute('aria-hidden', ariaHidden);
        });
        this.inertedElements = [];
        return;
      }

      this.inertedElements = [];
      let current = this.root;

      while (current && current !== document.body) {
        const parent = current.parentElement;
        if (!parent) break;

        Array.from(parent.children).forEach((sibling) => {
          if (sibling === current || !(sibling instanceof HTMLElement)) return;
          this.inertedElements.push({
            element: sibling,
            inert: sibling.inert,
            ariaHidden: sibling.getAttribute('aria-hidden'),
          });
          sibling.inert = true;
          sibling.setAttribute('aria-hidden', 'true');
        });

        current = parent;
      }
    }

    goToComments() {
      const moveToComments = () => {
        const comments = document.getElementById('comments');
        if (!comments) {
          this.announce('Yorum alanı bulunamadı.');
          return;
        }
        comments.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
        comments.querySelector('input, textarea, button, [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true });
      };

      if (this.isFullscreen) {
        this.setFullscreen(false, { restorePosition: false, focusExit: false, returnFocus: false });
        window.requestAnimationFrame(() => window.requestAnimationFrame(moveToComments));
      } else {
        moveToComments();
      }
    }

    setView(view, { restorePosition = true } = {}) {
      const nextView = view === 'book' ? 'book' : 'flow';
      if (this.state.view === nextView && !restorePosition) return;

      this.state.view = nextView;
      this.flow.hidden = nextView !== 'flow';
      this.book.hidden = nextView !== 'book';

      this.root.querySelectorAll('[data-reader-view-button]').forEach((button) => {
        const isActive = button.dataset.readerViewButton === nextView;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      this.persistState();

      if (nextView === 'book') {
        this.prepareBookPagination({ preserveAnchor: true });
        this.announce('Kitap görünümü açıldı. Sağ ve sol ok tuşlarıyla sayfalar arasında ilerleyebilirsiniz.');
      } else {
        window.requestAnimationFrame(() => {
          this.refreshAnchorOffsets();
          if (restorePosition && (this.currentAnchor || this.state.anchor)) {
            this.scrollToAnchor(this.currentAnchor || this.state.anchor, false);
          }
          this.updateFlowPosition();
        });
        this.announce('Akıcı okuma görünümü açıldı.');
      }
    }

    applyState({ restorePosition = false } = {}) {
      this.root.dataset.readerTheme = this.state.theme;
      this.root.dataset.readerWidth = this.state.width;
      this.root.style.setProperty('--reader-font-scale', String(this.state.fontScale));
      this.setView(this.state.view, { restorePosition });
      this.updateThemeButton();
      this.updateSoundButton();
    }

    setFontScale(value) {
      const next = Math.round(clamp(value, FONT_MIN, FONT_MAX) * 100) / 100;
      if (next === this.state.fontScale) return;

      if (this.state.view === 'book') {
        this.pendingBookPosition = this.pendingBookPosition
          || this.captureBookPosition()
          || this.state.bookPosition
          || null;
      }

      this.state.fontScale = next;
      this.root.style.setProperty('--reader-font-scale', String(next));
      this.persistState();
      this.refreshAnchorOffsets();
      if (this.state.view === 'book') this.schedulePagination();
      this.announce(`Yazı boyutu yüzde ${Math.round(next * 100)} olarak ayarlandı.`);
    }

    cycleTheme() {
      const currentIndex = THEMES.indexOf(this.state.theme);
      this.state.theme = THEMES[(currentIndex + 1) % THEMES.length];
      this.root.dataset.readerTheme = this.state.theme;
      this.persistState();
      this.updateThemeButton();
      if (this.state.view === 'book') this.schedulePagination();

      const labels = { paper: 'Kâğıt', sepia: 'Sepya', night: 'Gece' };
      this.announce(`${labels[this.state.theme]} okuma teması seçildi.`);
    }

    updateThemeButton() {
      const button = this.root.querySelector('[data-reader-action="theme"]');
      if (!button) return;
      const labels = { paper: 'Kâğıt', sepia: 'Sepya', night: 'Gece' };
      button.setAttribute('aria-label', `Okuma temasını değiştir. Geçerli tema: ${labels[this.state.theme]}`);
      button.title = `Tema: ${labels[this.state.theme]}`;
    }

    async togglePageSound() {
      const enabling = !this.state.soundEnabled;

      if (!enabling) {
        this.state.soundEnabled = false;
        this.persistState();
        this.updateSoundButton();
        this.announce('Sayfa çevirme sesi kapatıldı.');
        return;
      }

      const context = await this.unlockPageSound();
      if (!context || context.state !== 'running') {
        this.state.soundEnabled = false;
        this.persistState();
        this.updateSoundButton();
        this.announce('Tarayıcı sayfa sesini başlatamadı. Cihazın medya sesini ve tarayıcı izinlerini kontrol edin.');
        return;
      }

      this.state.soundEnabled = true;
      this.persistState();
      this.updateSoundButton();
      this.emitPageTurnSound(1, 0.72);
      this.announce('Sayfa çevirme sesi açıldı.');
    }

    updateSoundButton() {
      const button = this.root.querySelector('[data-reader-action="sound"]');
      if (!button) return;

      const enabled = this.state.soundEnabled === true;
      button.setAttribute('aria-pressed', String(enabled));
      button.setAttribute(
        'aria-label',
        enabled ? 'Sayfa çevirme sesini kapat' : 'Sayfa çevirme sesini aç',
      );
      button.title = enabled ? 'Sayfa sesi açık' : 'Sayfa sesi kapalı';
    }

    unlockPageSound() {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return Promise.resolve(null);

      if (!this.audioContext || this.audioContext.state === 'closed') {
        try {
          this.audioContext = new AudioContextClass({ latencyHint: 'interactive' });
        } catch (error) {
          try {
            this.audioContext = new AudioContextClass();
          } catch (fallbackError) {
            return Promise.resolve(null);
          }
        }
      }

      const context = this.audioContext;

      // iOS/Safari için gerçek kullanıcı dokunuşu içinde çok kısa bir kaynak başlatılır.
      // Bu kaynak duyulmayacak kadar kısadır; yalnız ses oturumunu etkinleştirir.
      try {
        const unlockBuffer = context.createBuffer(1, 1, context.sampleRate || 44100);
        const unlockSource = context.createBufferSource();
        const unlockGain = context.createGain();
        unlockSource.buffer = unlockBuffer;
        unlockGain.gain.value = 0.00001;
        unlockSource.connect(unlockGain);
        unlockGain.connect(context.destination);
        unlockSource.start(0);
      } catch (error) {
        // Resume denemesi yine yapılır.
      }

      if (context.state === 'running') return Promise.resolve(context);

      try {
        const resumeResult = context.resume();
        if (resumeResult && typeof resumeResult.then === 'function') {
          return resumeResult
            .then(() => context)
            .catch(() => null);
        }
      } catch (error) {
        return Promise.resolve(null);
      }

      return Promise.resolve(context.state === 'running' ? context : null);
    }

    emitPageTurnSound(direction, volume = 1) {
      const context = this.audioContext;
      if (!context || context.state !== 'running') return;

      const duration = 0.26;
      const frameCount = Math.max(1, Math.floor(context.sampleRate * duration));
      const buffer = context.createBuffer(1, frameCount, context.sampleRate);
      const channel = buffer.getChannelData(0);

      for (let index = 0; index < frameCount; index += 1) {
        const progress = index / frameCount;
        const attack = Math.min(1, progress / 0.06);
        const release = Math.pow(1 - progress, 1.55);
        const flutter = 0.78 + Math.sin(progress * Math.PI * 18) * 0.22;
        channel[index] = (Math.random() * 2 - 1) * attack * release * flutter;
      }

      const source = context.createBufferSource();
      source.buffer = buffer;

      const filter = context.createBiquadFilter();
      filter.type = 'bandpass';

      const gain = context.createGain();
      const now = context.currentTime;
      const peak = clamp(0.145 * Number(volume || 1), 0.02, 0.18);
      const startFrequency = direction > 0 ? 2300 : 1800;
      const endFrequency = direction > 0 ? 720 : 920;

      filter.frequency.setValueAtTime(startFrequency, now);
      filter.frequency.exponentialRampToValueAtTime(endFrequency, now + duration);
      filter.Q.setValueAtTime(0.48, now);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(peak, now + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      source.connect(filter);
      filter.connect(gain);
      gain.connect(context.destination);
      source.start(now);
      source.stop(now + duration + 0.02);
    }

    playPageTurnSound(direction) {
      if (!this.state.soundEnabled) return;

      if (this.audioContext?.state === 'running') {
        this.emitPageTurnSound(direction);
        return;
      }

      // Sayfa düğmesine dokunma da kullanıcı hareketidir; kesilen/suspended
      // ses oturumunu bu hareket içinde yeniden etkinleştir.
      void this.unlockPageSound().then((context) => {
        if (context?.state === 'running') this.emitPageTurnSound(direction);
      });
    }

    setToc(open) {
      if (!this.toc) return;
      this.toc.hidden = !open;
      const toggle = this.root.querySelector('[data-reader-action="toc"]');
      toggle?.setAttribute('aria-expanded', String(open));
      if (open) this.toc.querySelector('button')?.focus({ preventScroll: true });
    }

    getFirstAnchor() {
      const first = this.anchorElements[0];
      return first?.dataset?.readerAnchor || first?.id || this.flow.id;
    }

    setBookmark() {
      const anchor = this.currentAnchor || this.state.anchor || this.getFirstAnchor();
      if (!anchor) {
        this.announce('Yer imi oluşturulamadı.');
        return;
      }

      this.state.bookmark = anchor;
      this.state.anchor = anchor;
      this.persistState();
      this.updateContinueButton();
      this.announce('Yer imi kaydedildi.');
    }

    updateContinueButton() {
      if (!this.continueButton) return;
      const hasPosition = this.contentReady && Boolean(this.state.bookmark || this.state.anchor);
      this.continueButton.disabled = !hasPosition;
      this.continueButton.setAttribute('aria-disabled', String(!hasPosition));
    }

    goToAnchor(anchor) {
      if (!anchor) return;

      if (this.state.view === 'book') {
        if (!this.pages.length) this.buildPages({ preserveAnchor: false });
        const pageIndex = this.findPageForAnchor(anchor);
        if (pageIndex >= 0) {
          this.pageIndex = this.alignPageIndex(pageIndex);
          this.renderBookPages();
        }
      } else {
        this.scrollToAnchor(anchor, true);
      }
    }

    getElementScrollTop(element) {
      if (!this.isFullscreen) return element.getBoundingClientRect().top + window.scrollY;
      const rootRect = this.root.getBoundingClientRect();
      return element.getBoundingClientRect().top - rootRect.top + this.root.scrollTop;
    }

    getScrollMetrics() {
      if (this.isFullscreen) {
        return {
          scrollTop: this.root.scrollTop,
          viewportHeight: this.root.clientHeight || window.innerHeight,
        };
      }

      return {
        scrollTop: window.scrollY,
        viewportHeight: window.innerHeight,
      };
    }

    scrollToAnchor(anchor, smooth = true) {
      const target = document.getElementById(anchor)
        || this.root.querySelector(`[data-reader-anchor="${cssEscape(anchor)}"]`);
      if (!target) return;

      const toolbarHeight = this.toolbarWrap?.getBoundingClientRect().height || 0;
      const offset = toolbarHeight + 24;
      const targetTop = this.getElementScrollTop(target) - offset;
      const behavior = smooth && !this.reducedMotion ? 'smooth' : 'auto';

      if (this.isFullscreen) {
        this.root.scrollTo({ top: Math.max(0, targetTop), behavior });
      } else {
        window.scrollTo({ top: Math.max(0, targetTop), behavior });
      }

      this.currentAnchor = target.id || anchor;
      this.state.anchor = this.currentAnchor;
      this.persistState();
      this.updateContinueButton();
    }

    refreshAnchorOffsets() {
      this.anchorOffsets = this.anchorElements.map((element) => ({
        id: element.dataset.readerAnchor || element.id,
        top: this.getElementScrollTop(element),
      }));
    }

    updateFlowPosition() {
      if (this.state.view !== 'flow' || !this.anchorOffsets.length) return;

      const metrics = this.getScrollMetrics();
      const readingLine = metrics.scrollTop + metrics.viewportHeight * 0.34;
      let low = 0;
      let high = this.anchorOffsets.length - 1;
      let selected = 0;

      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        if (this.anchorOffsets[middle].top <= readingLine) {
          selected = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      const anchor = this.anchorOffsets[selected]?.id;
      if (anchor && anchor !== this.currentAnchor) {
        this.currentAnchor = anchor;
        this.state.anchor = anchor;
        this.persistState();
        this.updateContinueButton();
      }

      const flowTop = this.getElementScrollTop(this.flow);
      const available = Math.max(1, this.flow.scrollHeight - metrics.viewportHeight * 0.55);
      const current = readingLine - flowTop;
      const percentage = clamp((current / available) * 100, 0, 100);
      this.updateProgress(percentage);
    }

    updateProgress(percentage) {
      if (this.progressBar) this.progressBar.style.width = `${clamp(percentage, 0, 100).toFixed(2)}%`;
    }

    schedulePagination() {
      window.clearTimeout(this.paginationTimeout);
      this.paginationTimeout = window.setTimeout(() => this.prepareBookPagination({ preserveAnchor: true }), 160);
    }

    prepareBookPagination({ preserveAnchor = true } = {}) {
      const paginate = () => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.buildPages({ preserveAnchor }));
        });
      };

      if (document.fonts?.ready) {
        document.fonts.ready.then(paginate).catch(paginate);
      } else {
        paginate();
      }
    }

    showBookFallback(message) {
      if (this.leftPage) {
        this.leftPage.innerHTML = '';
        const wrapper = document.createElement('div');
        wrapper.className = 'mez-reader__book-fallback';
        const heading = document.createElement('h3');
        const paragraph = document.createElement('p');
        heading.textContent = 'Kitap görünümü hazırlanamadı';
        paragraph.textContent = message;
        wrapper.append(heading, paragraph);
        this.leftPage.appendChild(wrapper);
      }
      if (this.rightPage) this.rightPage.innerHTML = '';
      if (this.pageLabel) this.pageLabel.textContent = 'Akıcı okuma kullanılabilir';
      if (this.pageTotal) this.pageTotal.textContent = '';
      this.announce(message);
    }

    captureBookPosition() {
      if (!this.pages.length || !this.leftPage) return null;

      const pageRect = this.leftPage.getBoundingClientRect();
      const renderedNodes = Array.from(
        this.leftPage.querySelectorAll('[data-page-anchor][data-page-start][data-page-end]'),
      );

      if (renderedNodes.length && pageRect.height > 0) {
        // Sayfanın başı yerine okurun doğal okuma hattını sakla. Böylece A+/A−
        // sonrasında aynı paragrafın yalnız başına değil, yaklaşık aynı kelimesine dönülür.
        const readingLine = pageRect.top + pageRect.height * 0.38;
        let selectedNode = null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        renderedNodes.forEach((node) => {
          const rect = node.getBoundingClientRect();
          if (rect.height <= 0) return;

          if (readingLine >= rect.top && readingLine <= rect.bottom) {
            selectedNode = node;
            nearestDistance = 0;
            return;
          }

          if (nearestDistance === 0) return;
          const distance = readingLine < rect.top
            ? rect.top - readingLine
            : readingLine - rect.bottom;

          if (distance < nearestDistance) {
            nearestDistance = distance;
            selectedNode = node;
          }
        });

        if (selectedNode) {
          const anchor = selectedNode.dataset.pageAnchor || '';
          const start = Math.max(0, Number(selectedNode.dataset.pageStart) || 0);
          const end = Math.max(start + 1, Number(selectedNode.dataset.pageEnd) || start + 1);
          const rect = selectedNode.getBoundingClientRect();
          const ratio = rect.height > 0
            ? clamp((readingLine - rect.top) / rect.height, 0, 0.999)
            : 0;
          const offset = Math.min(end - 1, start + Math.floor((end - start) * ratio));

          if (anchor) return { anchor, offset };
        }
      }

      const page = this.pages[this.pageIndex];
      const fallback = page?.positions?.[0];
      if (!fallback?.anchor) return this.state.bookPosition || null;

      return {
        anchor: fallback.anchor,
        offset: Math.max(0, Number(fallback.start) || 0),
      };
    }

    findPageForPosition(position, pages = this.pages) {
      if (!position?.anchor || !pages.length) return -1;

      const targetOffset = Math.max(0, Number(position.offset) || 0);
      let nearestIndex = -1;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
        const ranges = pages[pageIndex].positions || [];

        for (const range of ranges) {
          if (range.anchor !== position.anchor) continue;

          if (targetOffset >= range.start && targetOffset < range.end) {
            return pageIndex;
          }

          const distance = Math.min(
            Math.abs(targetOffset - range.start),
            Math.abs(targetOffset - range.end),
          );

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = pageIndex;
          }
        }
      }

      return nearestIndex;
    }

    buildPages({ preserveAnchor = true } = {}) {
      if (this.isPaginating || this.book.hidden || !this.leftPage) return;

      const rect = this.leftPage.getBoundingClientRect();
      if (rect.width < 100 || rect.height < 200) {
        this.paginationAttempts += 1;
        if (this.paginationAttempts <= 12) {
          window.requestAnimationFrame(() => this.buildPages({ preserveAnchor }));
        } else {
          this.paginationAttempts = 0;
          this.showBookFallback('Sayfa ölçüleri alınamadı. Akıcı okuma görünümünü kullanabilirsiniz.');
        }
        return;
      }

      this.paginationAttempts = 0;

      if (!this.pageNodes.length) {
        this.showBookFallback('Bu kitap için sayfalanabilir metin bulunamadı.');
        return;
      }

      this.isPaginating = true;

      const preservedPosition = preserveAnchor
        ? (this.pendingBookPosition || this.captureBookPosition() || this.state.bookPosition)
        : null;
      const preservedAnchor = preserveAnchor ? (this.currentAnchor || this.state.anchor) : '';
      this.pendingBookPosition = null;

      const measure = document.createElement('div');
      measure.className = 'mez-reader__page mez-reader__measure';
      measure.setAttribute('aria-hidden', 'true');
      measure.style.display = 'block';
      measure.style.width = `${rect.width}px`;
      measure.style.height = `${rect.height}px`;
      this.book.appendChild(measure);

      const pages = [];
      let pageAnchors = [];
      let pagePositions = [];

      const pushPage = () => {
        if (!measure.children.length) return;
        pages.push({
          html: measure.innerHTML,
          anchors: Array.from(new Set(pageAnchors.filter(Boolean))),
          positions: pagePositions.map((position) => ({ ...position })),
        });
        measure.innerHTML = '';
        pageAnchors = [];
        pagePositions = [];
      };

      const sourceWordCount = (source) => Math.max(
        1,
        (source.textContent || '').trim().split(/\s+/).filter(Boolean).length,
      );

      const addClone = (source) => {
        const clone = source.cloneNode(true);
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.removeAttribute('data-reader-anchor');
        const anchor = source.dataset.readerAnchor || source.id || '';
        const end = sourceWordCount(source);
        if (anchor) {
          clone.dataset.pageAnchor = anchor;
          clone.dataset.pageStart = '0';
          clone.dataset.pageEnd = String(end);
        }
        measure.appendChild(clone);
        if (anchor) {
          pageAnchors.push(anchor);
          pagePositions.push({ anchor, start: 0, end });
        }
        return { clone, anchor };
      };

      const removeLastPosition = (anchor) => {
        if (!anchor) return;
        const anchorIndex = pageAnchors.lastIndexOf(anchor);
        if (anchorIndex >= 0) pageAnchors.splice(anchorIndex, 1);
        const positionIndex = pagePositions.map((position) => position.anchor).lastIndexOf(anchor);
        if (positionIndex >= 0) pagePositions.splice(positionIndex, 1);
      };

      const appendTextPiece = (source, anchor, words, start, count) => {
        const piece = source.cloneNode(false);
        piece.removeAttribute('id');
        piece.removeAttribute('data-reader-anchor');
        const end = start + count;
        if (anchor) {
          piece.dataset.pageAnchor = anchor;
          piece.dataset.pageStart = String(start);
          piece.dataset.pageEnd = String(end);
        }
        piece.textContent = words.slice(start, end).join(' ');
        measure.appendChild(piece);
        if (anchor) {
          pageAnchors.push(anchor);
          pagePositions.push({ anchor, start, end });
        }
      };

      const findBestWordCount = (source, anchor, words, start) => {
        let low = 1;
        let high = words.length - start;
        let best = 0;

        while (low <= high) {
          const middle = Math.floor((low + high) / 2);
          const test = source.cloneNode(false);
          test.removeAttribute('id');
          test.removeAttribute('data-reader-anchor');
          if (anchor) test.dataset.pageAnchor = anchor;
          test.textContent = words.slice(start, start + middle).join(' ');
          measure.appendChild(test);
          const fits = measure.scrollHeight <= measure.clientHeight + 2;
          test.remove();

          if (fits) {
            best = middle;
            low = middle + 1;
          } else {
            high = middle - 1;
          }
        }

        return best;
      };

      const splitTextNodeAcrossPages = (source, anchor) => {
        const words = (source.textContent || '').trim().split(/\s+/).filter(Boolean);
        if (!words.length) return;

        let offset = 0;

        while (offset < words.length) {
          let best = findBestWordCount(source, anchor, words, offset);

          if (best === 0) {
            if (measure.children.length) {
              pushPage();
              continue;
            }
            best = 1;
          }

          appendTextPiece(source, anchor, words, offset, best);
          offset += best;

          if (offset < words.length) pushPage();
        }
      };

      this.pageNodes.forEach((source) => {
        const canSplit = /^(P|BLOCKQUOTE|LI)$/i.test(source.tagName || '')
          && (source.textContent || '').trim().split(/\s+/).filter(Boolean).length > 1;

        const { clone, anchor } = addClone(source);

        if (measure.scrollHeight <= measure.clientHeight + 2) return;

        clone.remove();
        removeLastPosition(anchor);

        if (canSplit) {
          splitTextNodeAcrossPages(source, anchor);
          return;
        }

        if (measure.children.length) pushPage();

        const retry = addClone(source);
        if (measure.scrollHeight <= measure.clientHeight + 2) return;

        retry.clone.remove();
        removeLastPosition(retry.anchor);

        const forced = source.cloneNode(true);
        forced.removeAttribute('id');
        forced.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        if (retry.anchor) forced.dataset.pageAnchor = retry.anchor;
        measure.appendChild(forced);
        if (retry.anchor) {
          pageAnchors.push(retry.anchor);
          pagePositions.push({
            anchor: retry.anchor,
            start: 0,
            end: sourceWordCount(source),
          });
        }
      });

      pushPage();
      measure.remove();
      this.pages = pages;
      this.lastPageWidth = rect.width;
      this.isPaginating = false;

      if (!this.pages.length) {
        this.showBookFallback('Bu kitap için sayfalanabilir içerik bulunamadı.');
        return;
      }

      let matchedPage = -1;

      if (preservedPosition) {
        matchedPage = this.findPageForPosition(preservedPosition, this.pages);
      }

      if (matchedPage < 0 && preservedAnchor) {
        matchedPage = this.findPageForAnchor(preservedAnchor);
      }

      this.pageIndex = matchedPage >= 0
        ? this.alignPageIndex(matchedPage)
        : this.alignPageIndex(this.pageIndex);

      this.renderBookPages();
    }

    getSpreadSize() {
      return window.matchMedia('(min-width: 990px)').matches ? 2 : 1;
    }

    alignPageIndex(index) {
      const spreadSize = this.getSpreadSize();
      const maxIndex = Math.max(0, this.pages.length - 1);
      const safeIndex = clamp(Number(index) || 0, 0, maxIndex);
      return spreadSize === 2 ? Math.floor(safeIndex / 2) * 2 : safeIndex;
    }

    findPageForAnchor(anchor) {
      return this.pages.findIndex((page) => page.anchors.includes(anchor));
    }

    renderBookPages() {
      if (!this.pages.length) return;

      const spreadSize = this.getSpreadSize();
      this.pageIndex = this.alignPageIndex(this.pageIndex);
      const left = this.pages[this.pageIndex];
      const right = spreadSize === 2 ? this.pages[this.pageIndex + 1] : null;

      this.leftPage.innerHTML = left?.html || '';
      this.rightPage.innerHTML = right?.html || '';
      this.rightPage.setAttribute('aria-hidden', String(spreadSize !== 2 || !right));

      const firstPageNumber = this.pageIndex + 1;
      const lastPageNumber = Math.min(this.pageIndex + spreadSize, this.pages.length);
      if (this.pageLabel) {
        this.pageLabel.textContent = spreadSize === 2 && lastPageNumber > firstPageNumber
          ? `Sayfa ${firstPageNumber}–${lastPageNumber}`
          : `Sayfa ${firstPageNumber}`;
      }
      if (this.pageTotal) this.pageTotal.textContent = `/ ${this.pages.length}`;

      if (this.prevButton) this.prevButton.disabled = this.pageIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.pageIndex + spreadSize >= this.pages.length;

      const anchor = left?.anchors?.[0];
      if (anchor) {
        this.currentAnchor = anchor;
        this.state.anchor = anchor;
      }

      const capturedPosition = this.captureBookPosition();
      const fallbackPosition = left?.positions?.[0];
      this.state.bookPosition = capturedPosition?.anchor
        ? capturedPosition
        : fallbackPosition?.anchor
          ? {
            anchor: fallbackPosition.anchor,
            offset: Math.max(0, Number(fallbackPosition.start) || 0),
          }
          : null;

      this.state.pageIndex = this.pageIndex;
      this.persistState();
      this.updateContinueButton();
      this.updateProgress(((this.pageIndex + spreadSize) / this.pages.length) * 100);
    }

    turnPage(direction) {
      if (!this.pages.length) {
        this.buildPages({ preserveAnchor: true });
        return;
      }

      const spreadSize = this.getSpreadSize();
      const nextIndex = this.alignPageIndex(this.pageIndex + direction * spreadSize);
      if (nextIndex === this.pageIndex) return;

      this.playPageTurnSound(direction);

      const animationClass = direction > 0 ? 'is-turning-forward' : 'is-turning-back';
      const update = () => {
        this.pageIndex = nextIndex;
        this.renderBookPages();
      };

      if (this.reducedMotion) {
        update();
        return;
      }

      this.spread?.classList.add(animationClass);
      window.setTimeout(update, 180);
      window.setTimeout(() => this.spread?.classList.remove(animationClass), 410);
    }

    announce(message) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimeout);
      this.status.textContent = message;
      this.statusTimeout = window.setTimeout(() => {
        this.status.textContent = '';
      }, 4500);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => new MezettoEbookReader(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
